import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, ScrollText, Search, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { LigneReglement } from '../components/reglement-interieur/LigneReglement';
import { EnvoiSession } from '../components/reglement-interieur/EnvoiSession';
import { ouvrirPdf } from '../components/reglement-interieur/SignerReglementDialog';

const Chiffre = ({ valeur, label, testId }) => (
  <div className="rounded-xl bg-white/10 px-4 py-3">
    <p className="text-2xl font-bold" data-testid={testId}>{valeur}</p>
    <p className="text-xs text-white/70">{label}</p>
  </div>
);
const FILTRES = [['tous', 'Tous'], ['non_envoye', 'Non envoyé'], ['a_signer', 'À signer'], ['signe', 'Signé']];

export default function ReglementInterieur({ user }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [session, setSession] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [choix, setChoix] = useState([]);
  const [confirmer, setConfirmer] = useState(false);
  const charger = useCallback(() => { axios.get('/reglement-interieur/suivi').then(({ data: d }) => setData(d)).catch(() => setData({ erreur: true })); }, []);
  useEffect(() => { charger(); }, [charger]);

  if (!data) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (data.erreur) return <div className="p-8 text-red-600" data-testid="reglement-erreur">Accès non autorisé ou erreur de chargement.</div>;
  const peutEnvoyer = ['centre', 'direction'].includes(user?.role);
  const liste = data.stagiaires.filter((s) => (filtre === 'tous' || s.statut === filtre) && (!session || s.session === session)
    && `${s.prenom} ${s.nom}`.toLowerCase().includes(q.trim().toLowerCase()));
  const selectionnables = liste.filter((s) => s.statut !== 'signe').map((s) => s.user_id);
  const tout = selectionnables.length > 0 && selectionnables.every((id) => choix.includes(id));
  const cocher = (id) => setChoix((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const c = data.compteurs;

  const envoyer = async () => {
    try {
      const { data: d } = await axios.post('/reglement-interieur/envoyer', { stagiaire_ids: choix });
      toast.success(`Règlement envoyé à ${d.envoyes} stagiaire${d.envoyes > 1 ? 's' : ''} (e-mail + notification)`);
      if (d.sans_email?.length) toast.warning(`Sans e-mail (notification seulement) : ${d.sans_email.join(', ')}`);
      setChoix([]);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Envoi impossible');
    }
  };

  return (
    <div className="space-y-6" data-testid="reglement-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]"><ArrowLeft className="h-4 w-4" /> Tableau de bord</Link>
      <header className="rounded-2xl bg-[#334155] p-6 sm:p-8 text-white shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-xl bg-white/15 p-3"><ScrollText className="h-7 w-7" /></div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Administratif CFA</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Règlement intérieur</h1>
          </div>
          <button type="button" onClick={() => ouvrirPdf('/reglement-interieur/pdf', 'Reglement_interieur_FISL.pdf')} data-testid="reglement-voir-vierge"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10"><FileText className="h-4 w-4" /> Voir le règlement</button>
        </div>
        <p className="mt-3 text-sm text-white/70">Envoi à la rentrée (mi-mars) : chaque stagiaire reçoit un e-mail et une notification, coche la phrase d'acceptation et signe. Sans signature, relance automatique par e-mail à J+7 puis chaque semaine (3 relances au maximum). Les règlements signés vont dans le dossier Qualiopi (indicateur 9).</p>
        <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-xl">
          <Chiffre valeur={c.stagiaires} label="Stagiaires" testId="reglement-total" />
          <Chiffre valeur={c.envoyes} label="Envoyés" testId="reglement-envoyes" />
          <Chiffre valeur={`${c.signes}/${c.stagiaires}`} label="Signés" testId="reglement-signes" />
        </div>
      </header>
      {peutEnvoyer && <EnvoiSession data={data} onEnvoye={charger} />}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un stagiaire" className="pl-9" data-testid="reglement-recherche" />
        </div>
        <select value={session} onChange={(e) => setSession(e.target.value)} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="reglement-session">
          <option value="">Toutes les sessions</option>
          {data.sessions.map((s) => <option key={s} value={s}>Session {s}</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          {FILTRES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFiltre(k)} aria-pressed={filtre === k} data-testid={`reglement-filtre-${k}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${filtre === k ? 'bg-[#334155] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'}`}>{l}</button>
          ))}
        </div>
      </div>
      {peutEnvoyer && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-orange-50 px-4 py-3 ring-1 ring-orange-200">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={tout} onChange={() => setChoix(tout ? [] : [...new Set([...choix, ...selectionnables])])} className="h-4 w-4 accent-orange-500" data-testid="reglement-tout-cocher" />
            Tout cocher (non signés affichés)
          </label>
          <span className="flex-1 text-sm text-slate-500" data-testid="reglement-nb-choix">{choix.length} sélectionné{choix.length > 1 ? 's' : ''}</span>
          <button type="button" disabled={!choix.length} onClick={() => setConfirmer(true)} data-testid="reglement-envoyer"
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2 text-sm font-bold text-white shadow transition-colors hover:bg-orange-600 disabled:opacity-40"><Send className="h-4 w-4" /> Envoyer le règlement</button>
        </div>
      )}
      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {liste.map((s) => <LigneReglement key={s.user_id} s={s} selection={peutEnvoyer} coche={choix.includes(s.user_id)} onCocher={() => cocher(s.user_id)}
          onVoir={(x) => ouvrirPdf(`/reglement-interieur/signe/${x.user_id}`, `Reglement_interieur_${x.nom}_${x.prenom}.pdf`)} />)}
        {!liste.length && <li className="p-8 text-center text-sm text-slate-400" data-testid="reglement-vide">Aucun stagiaire</li>}
      </ul>
      <AlertDialog open={confirmer} onOpenChange={setConfirmer}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Envoyer le règlement intérieur ?</AlertDialogTitle>
            <AlertDialogDescription>{choix.length} stagiaire{choix.length > 1 ? 's recevront' : ' recevra'} un e-mail et une notification pour lire et signer le règlement.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reglement-annuler">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={envoyer} data-testid="reglement-confirmer">Envoyer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
